import React from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart3,
  ChartGantt,
  ChevronLeft,
  ChevronRight,
  FileText,
  KanbanSquare,
  LayoutDashboard,
  MousePointer2,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { APP_Z_INDEX } from '@/ui/theme/layers';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

export const SELF_SERVICE_GUIDED_TOUR_TEMPLATE = 'self_service_guided_v1';
export const SELF_SERVICE_GUIDED_TOUR_STORAGE_PREFIX = 'management-tool.selfServiceGuidedTour.v1';
export const SELF_SERVICE_GUIDED_TOUR_DIALOG_ID = 'self-service-guided-tour-dialog';
export const SELF_SERVICE_GUIDE_HREF = `${import.meta.env.BASE_URL}help/getting-started.ko.html`;

export type SelfServiceGuidedTourStepId = 'dashboard' | 'gantt' | 'kanban' | 'docs' | 'reports';

type TourStorageStatus = 'dismissed' | 'completed';

type TourStorageState = {
  status: TourStorageStatus;
  stepId?: string;
  updatedAt: string;
};

type TourStep = {
  id: SelfServiceGuidedTourStepId;
  view?: string;
  title: string;
  body: string;
  guide: string[];
  targetSelector: string;
  actionLabel: string;
  icon: LucideIcon;
};

type RectSnapshot = {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

type BlurPane = {
  id: 'top' | 'bottom' | 'left' | 'right';
  style: React.CSSProperties;
};

interface SelfServiceGuidedTourProps {
  isEligible: boolean;
  userId: string | null | undefined;
  projectId: string | null | undefined;
  onboardingTemplate: string;
  onboardingStatus: string;
  activeView: string;
  openRequestToken: number;
  initialStepId?: SelfServiceGuidedTourStepId | null;
  autoOpen?: boolean;
  onNavigateToView: (view: string) => void;
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));
const FOCUSABLE_TOUR_ELEMENT_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const snapshotRect = (element: Element | null): RectSnapshot | null => {
  if (!element) {
    return null;
  }

  const rect = element.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height,
  };
};

const fallbackRect = (): RectSnapshot => {
  const width = 180;
  const height = 48;
  const left = Math.max(16, (window.innerWidth - width) / 2);
  const top = Math.max(80, Math.min(window.innerHeight - height - 80, 160));
  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
  };
};

const overlaps = (
  a: { left: number; top: number; right: number; bottom: number },
  b: { left: number; top: number; right: number; bottom: number },
) => !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);

const overlapArea = (
  a: { left: number; top: number; right: number; bottom: number },
  b: { left: number; top: number; right: number; bottom: number },
) => {
  const width = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const height = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return width * height;
};

const getTourStorageKey = (
  userId: string | null | undefined,
  projectId: string | null | undefined,
  onboardingTemplate: string,
): string | null => {
  if (!userId || !projectId || onboardingTemplate !== SELF_SERVICE_GUIDED_TOUR_TEMPLATE) {
    return null;
  }
  return `${SELF_SERVICE_GUIDED_TOUR_STORAGE_PREFIX}:${userId}:${projectId}:${onboardingTemplate}`;
};

const readTourStorage = (storageKey: string | null): TourStorageState | null => {
  if (!storageKey || typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<TourStorageState>;
    if (parsed.status === 'dismissed' || parsed.status === 'completed') {
      return {
        status: parsed.status,
        stepId: typeof parsed.stepId === 'string' ? parsed.stepId : undefined,
        updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : '',
      };
    }
  } catch {
    return null;
  }
  return null;
};

const writeTourStorage = (storageKey: string | null, state: Omit<TourStorageState, 'updatedAt'>) => {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        ...state,
        updatedAt: new Date().toISOString(),
      }),
    );
  } catch {
    // Tour persistence is convenience-only; the app must remain usable.
  }
};

const isSetupFinished = (status: string) => status === 'completed' || status === 'skipped';

const getCardStyle = (targetRect: RectSnapshot): React.CSSProperties => {
  const margin = 16;
  const gap = 12;
  const cardWidth = Math.min(386, Math.max(304, window.innerWidth - margin * 2));
  const cardHeight = 318;
  const maxLeft = Math.max(margin, window.innerWidth - cardWidth - margin);
  const maxTop = Math.max(margin, window.innerHeight - cardHeight - margin);
  const targetBox = {
    left: targetRect.left - 8,
    top: targetRect.top - 8,
    right: targetRect.right + 8,
    bottom: targetRect.bottom + 8,
  };

  const candidates = [
    {
      left: targetRect.left,
      top: targetRect.bottom + gap,
    },
    {
      left: targetRect.left + (targetRect.width / 2) - (cardWidth / 2),
      top: targetRect.bottom + gap,
    },
    {
      left: targetRect.right + gap,
      top: targetRect.top,
    },
    {
      left: targetRect.left - cardWidth - gap,
      top: targetRect.top,
    },
    {
      left: targetRect.left,
      top: targetRect.top - cardHeight - gap,
    },
    {
      left: margin,
      top: margin,
    },
    {
      left: maxLeft,
      top: margin,
    },
    {
      left: margin,
      top: maxTop,
    },
    {
      left: maxLeft,
      top: maxTop,
    },
    {
      left: (window.innerWidth - cardWidth) / 2,
      top: maxTop,
    },
  ].map((candidate) => ({
    left: clamp(candidate.left, margin, maxLeft),
    top: clamp(candidate.top, margin, maxTop),
  }));

  const selected = candidates.find((candidate) => {
    const cardBox = {
      left: candidate.left,
      top: candidate.top,
      right: candidate.left + cardWidth,
      bottom: candidate.top + cardHeight,
    };
    return !overlaps(cardBox, targetBox);
  }) || candidates
    .map((candidate) => {
      const cardBox = {
        left: candidate.left,
        top: candidate.top,
        right: candidate.left + cardWidth,
        bottom: candidate.top + cardHeight,
      };
      const targetCenterX = targetBox.left + ((targetBox.right - targetBox.left) / 2);
      const targetCenterY = targetBox.top + ((targetBox.bottom - targetBox.top) / 2);
      const cardCenterX = cardBox.left + (cardWidth / 2);
      const cardCenterY = cardBox.top + (cardHeight / 2);
      const distance = Math.hypot(cardCenterX - targetCenterX, cardCenterY - targetCenterY);
      return {
        ...candidate,
        overlap: overlapArea(cardBox, targetBox),
        distance,
      };
    })
    .sort((a, b) => a.overlap - b.overlap || b.distance - a.distance)[0];

  return {
    left: selected.left,
    top: selected.top,
    width: cardWidth,
    maxHeight: Math.max(260, window.innerHeight - margin * 2),
  };
};

const getSpotlightFrame = (targetRect: RectSnapshot): RectSnapshot => {
  const inset = 6;
  const left = clamp(targetRect.left - inset, 0, window.innerWidth);
  const top = clamp(targetRect.top - inset, 0, window.innerHeight);
  const right = clamp(targetRect.right + inset, left, window.innerWidth);
  const bottom = clamp(targetRect.bottom + inset, top, window.innerHeight);

  return {
    left,
    top,
    right,
    bottom,
    width: Math.max(36, right - left),
    height: Math.max(30, bottom - top),
  };
};

const getBlurPanes = (spotlight: RectSnapshot): BlurPane[] => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const top = clamp(spotlight.top, 0, viewportHeight);
  const bottom = clamp(spotlight.bottom, top, viewportHeight);
  const left = clamp(spotlight.left, 0, viewportWidth);
  const right = clamp(spotlight.right, left, viewportWidth);

  return [
    {
      id: 'top',
      style: {
        left: 0,
        top: 0,
        width: viewportWidth,
        height: top,
      },
    },
    {
      id: 'bottom',
      style: {
        left: 0,
        top: bottom,
        width: viewportWidth,
        height: Math.max(0, viewportHeight - bottom),
      },
    },
    {
      id: 'left',
      style: {
        left: 0,
        top,
        width: left,
        height: Math.max(0, bottom - top),
      },
    },
    {
      id: 'right',
      style: {
        left: right,
        top,
        width: Math.max(0, viewportWidth - right),
        height: Math.max(0, bottom - top),
      },
    },
  ];
};

const ACTIVE_TARGET_SELECTOR = '[data-tour-active-target="true"]';

export const SelfServiceGuidedTour: React.FC<SelfServiceGuidedTourProps> = ({
  isEligible,
  userId,
  projectId,
  onboardingTemplate,
  onboardingStatus,
  activeView,
  openRequestToken,
  initialStepId = null,
  autoOpen = false,
  onNavigateToView,
}) => {
  const { t } = useSettings();
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = React.useState(false);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [liveStepStatus, setLiveStepStatus] = React.useState('');
  const [targetRect, setTargetRect] = React.useState<RectSnapshot | null>(null);
  const autoCheckedKeyRef = React.useRef<string | null>(null);
  const handledRequestTokenRef = React.useRef(openRequestToken);
  const dialogRef = React.useRef<HTMLElement | null>(null);
  const returnFocusElementRef = React.useRef<HTMLElement | null>(null);
  const shouldAnnounceStepRef = React.useRef(false);
  const dialogId = React.useId();
  const storageKey = React.useMemo(
    () => getTourStorageKey(userId, projectId, onboardingTemplate),
    [onboardingTemplate, projectId, userId],
  );
  const canUseTour = isEligible && Boolean(storageKey) && isSetupFinished(onboardingStatus);

  const steps = React.useMemo<TourStep[]>(
    () => [
      {
        id: 'dashboard',
        view: 'dashboard',
        title: t('Create the project brief'),
        body: t('Your project starts in setup: name the workspace, write the outcome, and add the first tasks. Dashboard is the control room after that.'),
        guide: [
          t('Confirm the workspace name and outcome match the project you meant to create.'),
          t('Read Project Health, Progress, Milestones, and Action Items before changing work.'),
          t('If health is at risk, fix the plan in Gantt or Kanban before reporting.'),
        ],
        targetSelector: '[data-tour-target="nav-dashboard"], [data-tour-target="dashboard-surface"], [data-testid="dashboard-surface-toolbar"]',
        actionLabel: t('Review Dashboard'),
        icon: LayoutDashboard,
      },
      {
        id: 'gantt',
        view: 'gantt',
        title: t('Build the timeline'),
        body: t('Use Gantt when the project needs a real plan, not just a task list. This is where scope becomes dates.'),
        guide: [
          t('Turn the outcome into phases, milestones, and dated tasks.'),
          t('Drag or edit bars when scope or deadlines change.'),
          t('Use the timeline to answer what is late, blocked, or next.'),
        ],
        targetSelector: '[data-tour-target="nav-gantt"]',
        actionLabel: t('Open Gantt'),
        icon: ChartGantt,
      },
      {
        id: 'kanban',
        view: 'kanban',
        title: t('Move work through execution'),
        body: t('Kanban is the daily operating board. Create, edit, and finish the first task here after the setup plan is ready.'),
        guide: [
          t('Use the + button beside To Do to add a New Task card, then open it.'),
          t('Open the card, edit Task title or the details you need, then press Enter to save the title.'),
          t('Select Done only when the deliverable is real, then refresh to confirm it stays finished.'),
        ],
        targetSelector: '[data-tour-target="nav-kanban"]',
        actionLabel: t('Open Kanban'),
        icon: KanbanSquare,
      },
      {
        id: 'docs',
        view: 'docs',
        title: t('Capture the source of truth'),
        body: t('Docs should explain the work so tasks and reports are not carrying context they cannot hold.'),
        guide: [
          t('Create the kickoff note, decision log, or meeting notes here.'),
          t('Write the why, owner, decision, and follow-up before it becomes memory.'),
          t('Use Docs before Reports so the update has a source to summarize.'),
        ],
        targetSelector: '[data-tour-target="nav-docs"]',
        actionLabel: t('Open Docs'),
        icon: FileText,
      },
      {
        id: 'reports',
        view: 'reports',
        title: t('Publish the update'),
        body: t('Reports are the outward-facing story: what changed, what is next, and what needs attention.'),
        guide: [
          t('Update tasks and docs first so the report starts from current truth.'),
          t('Use summary, milestones, risks, and decisions to write the stakeholder version.'),
          t('Send the report only after the work views agree with the story.'),
        ],
        targetSelector: '[data-tour-target="nav-reports"]',
        actionLabel: t('Open Reports'),
        icon: BarChart3,
      },
    ],
    [t],
  );

  const currentStep = steps[stepIndex] || steps[0];
  const currentIcon = currentStep.icon;
  const progressText = t('Step {{current}} of {{total}}', {
    current: stepIndex + 1,
    total: steps.length,
  });
  const currentStepAnnouncement = t('Step {{current}} of {{total}}: {{title}}', {
    current: stepIndex + 1,
    total: steps.length,
    title: currentStep.title,
  });

  const captureReturnFocusTarget = React.useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const activeElement = document.activeElement;
    returnFocusElementRef.current = activeElement instanceof HTMLElement && activeElement !== document.body
      ? activeElement
      : null;
  }, []);

  const restoreReturnFocus = React.useCallback(() => {
    const returnFocusElement = returnFocusElementRef.current;
    returnFocusElementRef.current = null;
    if (
      !returnFocusElement
      || typeof window === 'undefined'
      || typeof document === 'undefined'
      || !document.contains(returnFocusElement)
    ) {
      return;
    }

    window.setTimeout(() => {
      returnFocusElement.focus();
    }, 0);
  }, []);

  const openAtRequestedStep = React.useCallback(() => {
    const stored = readTourStorage(storageKey);
    const requestedStepIndex = initialStepId
      ? steps.findIndex((step) => step.id === initialStepId)
      : -1;
    const storedStepIndex = stored?.status === 'dismissed' && stored.stepId
      ? steps.findIndex((step) => step.id === stored.stepId)
      : -1;
    captureReturnFocusTarget();
    setStepIndex(requestedStepIndex >= 0 ? requestedStepIndex : storedStepIndex >= 0 ? storedStepIndex : 0);
    setIsOpen(true);
  }, [captureReturnFocusTarget, initialStepId, steps, storageKey]);

  React.useEffect(() => {
    if (!canUseTour || !storageKey) {
      setIsOpen(false);
      restoreReturnFocus();
      return;
    }

    if (!autoOpen) {
      return;
    }

    if (autoCheckedKeyRef.current === storageKey) {
      return;
    }
    autoCheckedKeyRef.current = storageKey;

    const stored = readTourStorage(storageKey);
    if (stored?.status === 'dismissed' || stored?.status === 'completed') {
      return;
    }

    returnFocusElementRef.current = null;
    setStepIndex(0);
    setIsOpen(true);
  }, [autoOpen, canUseTour, restoreReturnFocus, storageKey]);

  React.useEffect(() => {
    if (!canUseTour || openRequestToken === handledRequestTokenRef.current) {
      return;
    }
    handledRequestTokenRef.current = openRequestToken;
    openAtRequestedStep();
  }, [canUseTour, openAtRequestedStep, openRequestToken]);

  React.useEffect(() => {
    if (!isOpen || !currentStep) {
      return;
    }

    if (currentStep.view && activeView !== currentStep.view) {
      onNavigateToView(currentStep.view);
    }

  }, [activeView, currentStep, isOpen, onNavigateToView]);

  React.useEffect(() => {
    if (!isOpen) {
      shouldAnnounceStepRef.current = false;
      setLiveStepStatus('');
      return;
    }

    if (!shouldAnnounceStepRef.current) {
      return;
    }

    setLiveStepStatus((current) => {
      const next = currentStepAnnouncement;
      return current === next ? current : next;
    });
  }, [currentStepAnnouncement, isOpen]);

  React.useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      dialogRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [isOpen]);

  React.useLayoutEffect(() => {
    if (!isOpen || !currentStep || typeof window === 'undefined') {
      return;
    }

    let isCancelled = false;
    let highlightedElement: HTMLElement | null = null;
    const setHighlightedElement = (element: Element | null) => {
      const nextElement = element instanceof HTMLElement ? element : null;
      if (highlightedElement && highlightedElement !== nextElement) {
        highlightedElement.removeAttribute('data-tour-active-target');
      }
      if (nextElement && nextElement.getAttribute('data-tour-active-target') !== 'true') {
        nextElement.setAttribute('data-tour-active-target', 'true');
      }
      highlightedElement = nextElement;
    };
    const measure = () => {
      if (isCancelled) {
        return;
      }
      const element = document.querySelector(currentStep.targetSelector);
      setHighlightedElement(element);
      setTargetRect(snapshotRect(element) || fallbackRect());
    };
    measure();
    const timers = [80, 240, 520].map((delay) => window.setTimeout(measure, delay));
    const observer = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(measure)
      : null;
    observer?.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);

    return () => {
      isCancelled = true;
      setHighlightedElement(null);
      timers.forEach((timer) => window.clearTimeout(timer));
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [currentStep, isOpen]);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        writeTourStorage(storageKey, { status: 'dismissed', stepId: currentStep.id });
        setIsOpen(false);
        restoreReturnFocus();
        return;
      }

      if (event.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) {
          return;
        }

        const focusableElements = Array.from(
          dialog.querySelectorAll<HTMLElement>(FOCUSABLE_TOUR_ELEMENT_SELECTOR),
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (!firstElement || !lastElement) {
          event.preventDefault();
          dialog.focus();
          return;
        }

        const activeElement = document.activeElement;
        if (!dialog.contains(activeElement)) {
          event.preventDefault();
          (event.shiftKey ? lastElement : firstElement).focus();
          return;
        }

        if (activeElement === dialog) {
          event.preventDefault();
          (event.shiftKey ? lastElement : firstElement).focus();
          return;
        }

        if (event.shiftKey && activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
          return;
        }

        if (!event.shiftKey && activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStep.id, isOpen, restoreReturnFocus, storageKey]);

  if (!canUseTour || !isOpen || typeof document === 'undefined') {
    return null;
  }

  const rect = targetRect || fallbackRect();
  const spotlightFrame = getSpotlightFrame(rect);
  const spotlightStyle: React.CSSProperties = {
    left: spotlightFrame.left,
    top: spotlightFrame.top,
    width: spotlightFrame.width,
    height: spotlightFrame.height,
  };
  const blurPanes = getBlurPanes(spotlightFrame);
  const cursorStyle: React.CSSProperties = {
    transform: `translate3d(${rect.left + Math.max(12, rect.width * 0.72)}px, ${rect.top + Math.max(12, rect.height * 0.58)}px, 0)`,
  };
  const cardStyle = getCardStyle(rect);
  const isLastStep = stepIndex === steps.length - 1;
  const dialogTitleId = `${dialogId}-self-service-guided-tour-title`;
  const dialogDescriptionId = `${dialogId}-self-service-guided-tour-description`;
  const dialogProgressId = `${dialogId}-self-service-guided-tour-progress`;
  const previousStep = steps[stepIndex - 1];
  const nextStep = steps[stepIndex + 1];
  const backButtonLabel = previousStep
    ? t('Back to step {{step}}: {{title}}', {
      step: stepIndex,
      title: previousStep.title,
    })
    : t('Back');
  const nextButtonLabel = isLastStep
    ? t('Finish tour: {{title}}', { title: currentStep.title })
    : nextStep
      ? t('Next to step {{step}}: {{title}}', {
        step: stepIndex + 2,
        title: nextStep.title,
      })
      : t('Next');

  const handleDismiss = () => {
    writeTourStorage(storageKey, { status: 'dismissed', stepId: currentStep.id });
    setIsOpen(false);
    restoreReturnFocus();
  };

  const handlePrimaryAction = () => {
    if (currentStep.view) {
      onNavigateToView(currentStep.view);
    }
  };

  const handleNext = () => {
    if (isLastStep) {
      writeTourStorage(storageKey, { status: 'completed', stepId: currentStep.id });
      setIsOpen(false);
      restoreReturnFocus();
      return;
    }
    shouldAnnounceStepRef.current = true;
    setStepIndex((current) => Math.min(current + 1, steps.length - 1));
  };

  const handleBack = () => {
    shouldAnnounceStepRef.current = true;
    setStepIndex((current) => Math.max(0, current - 1));
  };

  return createPortal(
    <div
      data-testid="self-service-guided-tour-root"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: APP_Z_INDEX.headerDropdown + 40 }}
    >
      <style>
        {`${ACTIVE_TARGET_SELECTOR} {
          position: relative !important;
          z-index: ${APP_Z_INDEX.headerDropdown + 39} !important;
          outline: 1px solid rgba(59, 130, 246, 0.95);
          outline-offset: 1px;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.22), 0 8px 20px rgba(15, 23, 42, 0.22);
        }`}
      </style>
      {blurPanes.map((pane) => (
        <div
          key={pane.id}
          data-testid={`self-service-tour-blur-pane-${pane.id}`}
          data-tour-blur-pane={pane.id}
          className="pointer-events-auto absolute bg-black/10 backdrop-blur-[2px] transition-opacity duration-150 ease-out dark:bg-black/25 motion-reduce:transition-none"
          style={pane.style}
          aria-hidden="true"
        />
      ))}
      <div
        data-testid="self-service-tour-spotlight"
        className="absolute rounded-md border-2 border-blue-500/90 bg-blue-500/5 shadow-[0_0_0_1px_rgba(37,99,235,0.18),0_10px_28px_rgba(15,23,42,0.22)] transition-opacity duration-150 ease-out motion-reduce:transition-none"
        style={spotlightStyle}
        aria-hidden="true"
      />
      <div
        data-testid="self-service-tour-cursor"
        className="absolute left-0 top-0 text-blue-500 drop-shadow transition-transform duration-500 ease-out motion-reduce:transition-none"
        style={cursorStyle}
        aria-hidden="true"
      >
        <MousePointer2 className="h-6 w-6 fill-blue-500/15" />
      </div>

      <section
        id={SELF_SERVICE_GUIDED_TOUR_DIALOG_ID}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        aria-describedby={`${dialogDescriptionId} ${dialogProgressId}`}
        tabIndex={-1}
        data-testid="self-service-guided-tour"
        className={cn(
          'pointer-events-auto absolute overflow-auto rounded-lg border p-3 shadow-xl transition-opacity duration-150 ease-out motion-reduce:transition-none',
          theme.bgPrimary,
          theme.borderPrimary,
        )}
        style={cardStyle}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2.5">
            <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-md', theme.accentBlue)}>
              {React.createElement(currentIcon, { className: 'h-4 w-4', 'aria-hidden': true })}
            </div>
            <div className="min-w-0">
              <p
                data-testid="self-service-tour-live-step-status"
                className="sr-only"
                aria-live="polite"
                aria-atomic="true"
              >
                {liveStepStatus}
              </p>
              <p
                id={dialogProgressId}
                className={cn('text-[11px] font-semibold uppercase', theme.infoState)}
              >
                {progressText}
              </p>
              <h2
                id={dialogTitleId}
                className={cn('mt-1 text-sm font-semibold leading-5', theme.textPrimary)}
              >
                {currentStep.title}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label={t('Dismiss tour')}
            title={t('Dismiss tour')}
            className={cn('inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors', theme.surfaceHover, theme.textSecondary)}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <p
          id={dialogDescriptionId}
          className={cn('mt-3 text-sm leading-5', theme.textSecondary)}
        >
          {currentStep.body}
        </p>

        <ol
          className="mt-3 space-y-1.5"
          aria-label={t('Step checklist')}
          data-testid="self-service-tour-guide-list"
        >
          {currentStep.guide.map((item, index) => (
            <li key={item} className={cn('grid grid-cols-[18px_1fr] gap-2 text-xs leading-4', theme.textSecondary)}>
              <span
                className="mt-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-500/15 text-[10px] font-semibold text-blue-500"
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ol>

        <div
          className="mt-3 flex gap-1"
          role="progressbar"
          aria-labelledby={dialogProgressId}
          aria-valuemin={1}
          aria-valuemax={steps.length}
          aria-valuenow={stepIndex + 1}
          aria-valuetext={progressText}
        >
          {steps.map((step, index) => (
            <span
              key={step.id}
              aria-hidden="true"
              className={cn(
                'h-1 flex-1 rounded-full',
                index <= stepIndex ? 'bg-blue-500' : 'bg-zinc-200 dark:bg-zinc-700',
              )}
            />
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={handleDismiss}
            className={cn('h-8 rounded-md px-2.5 text-xs font-medium transition-colors', theme.surfaceHover, theme.textSecondary)}
          >
            {t('Skip tour')}
          </button>

          <div className="ml-auto flex items-center gap-2">
            {stepIndex > 0 ? (
              <button
                type="button"
                onClick={handleBack}
                aria-label={backButtonLabel}
                className={cn('inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-medium transition-colors', theme.surfaceHover, theme.textSecondary)}
              >
                <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                {t('Back')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={handlePrimaryAction}
              className={cn('h-8 rounded-md px-2.5 text-xs font-medium transition-colors', theme.surfaceHover, theme.textPrimary)}
            >
              {currentStep.actionLabel}
            </button>
            <button
              type="button"
              onClick={handleNext}
              aria-label={nextButtonLabel}
              className={cn('inline-flex h-8 items-center gap-1 rounded-md px-3 text-xs font-semibold transition-colors', theme.primaryAction)}
            >
              {isLastStep ? t('Finish tour') : t('Next')}
              {!isLastStep ? <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" /> : null}
            </button>
          </div>
        </div>
        <a
          href={SELF_SERVICE_GUIDE_HREF}
          target="_blank"
          rel="noopener"
          className={cn('mt-3 inline-flex text-xs font-medium underline underline-offset-2 transition-colors', theme.textSecondary)}
        >
          {t('Open beginner guide')}
        </a>
      </section>
    </div>,
    document.body,
  );
};
